import { useCallback, useEffect, useState } from 'react';
import { diagnostic } from '../utils/diagnostics';
import { db, dbKidData } from '../services/supabase';

/**
 * Shared balance hook for both Parent and Kid dashboards.
 *
 * IMPORTANT:
 * - Parent pages must use source = 'parent'
 * - Kid pages must use source = 'kid'
 *
 * This keeps the Parent and Kid Supabase sessions completely separate.
 */
export const useBalance = (childId, familyId, source = 'parent') => {
  const [balance, setBalance] = useState(0);
  const [savingsBalance, setSavingsBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const calculateBalance = async () => {
      // If the required IDs are not available yet, do not query.
      if (!childId || !familyId) {
        if (isMounted) {
          setBalance(0);
          setSavingsBalance(0);
          setError(null);
          setLoading(false);
        }

        return;
      }

      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
        }

        /**
         * Pick the correct database client.
         *
         * Parent Dashboard:
         *   db -> Parent Supabase client/session
         *
         * Kid Dashboard:
         *   dbKidData -> Kid Supabase client/session
         */
        const selectedDb = source === 'kid' ? dbKidData : db;

        const [transactions, savingsEntries] = await Promise.all([
          selectedDb.getTransactions(familyId, childId),
          selectedDb.getSavingsLedger(childId),
        ]);

        /**
         * Only approved transactions affect the child's balance.
         *
         * Pending and rejected transactions are intentionally ignored.
         */
        const approvedTransactions = (transactions || []).filter(
          (transaction) => transaction.approval_status === 'approved'
        );

        let totalTransactionBalance = 0;

        approvedTransactions.forEach((transaction) => {
          const amount = Number(transaction.amount_cents) || 0;

          if (transaction.type === 'income') {
            totalTransactionBalance += amount;
          } else if (transaction.type === 'expense') {
            totalTransactionBalance -= amount;
          }
        });

        /**
         * Calculate the amount currently held in savings.
         */
        let totalSavings = 0;

        (savingsEntries || []).forEach((entry) => {
          const amount = Number(entry.amount_cents) || 0;

          if (entry.direction === 'deposit') {
            totalSavings += amount;
          } else if (entry.direction === 'withdraw' || entry.direction === 'withdrawal') {
            totalSavings -= amount;
          }
        });

        /**
         * Spendable balance =
         * approved transaction balance - money moved into savings.
         *
         * Savings is displayed separately.
         */
        // Do not hide an accounting deficit by clamping it to $0.
        // New kid expenses are blocked before they can overspend, while any
        // existing negative balance remains visible so it can be corrected.
        const spendableBalance = totalTransactionBalance - totalSavings;

        if (isMounted) {
          setBalance(spendableBalance);
          setSavingsBalance(Math.max(0, totalSavings));
          setError(null);
        }
      } catch (err) {
        diagnostic('error', 'balance.calculation_failed', { operation: source === 'kid' ? 'kid' : 'parent' });

        /**
         * Do NOT reset the numbers to $0 on an error.
         *
         * A database/auth failure should not look like the child
         * legitimately has a zero balance.
         */
        if (isMounted) {
          setError(
            err?.message ||
              'Unable to calculate the current balance. Please try again.'
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    calculateBalance();

    return () => {
      isMounted = false;
    };
  }, [childId, familyId, source, refreshToken]);

  const refresh = useCallback(() => {
    setRefreshToken((value) => value + 1);
  }, []);

  return {
    balance,
    savingsBalance,
    loading,
    error,
    refresh,
  };
};
